// src/services/httpUtils.js
//
// Shared HTTP / request validation helpers
// ========================================
//
// PURPOSE
// -------
// This file provides small, reusable helpers used across frontend services
// and pages that communicate with the backend.
//
// CURRENT APP ARCHITECTURE
// ------------------------
// Your frontend now has a much clearer structure:
//
// - apiClient.js
//     owns authentication headers and refresh-on-401
//
// - AuthContext.js
//     owns React auth state and local-only cleanup behavior
//
// - service files
//     should stay thin and transport-focused
//
// - pages/components
//     decide how to react to errors (for example softLogout on 401)
//
// Because of that, this file should focus only on:
// - request cancellation detection
// - lightweight argument validation
// - stable parsing helpers for shared route keys like "todo:123" or "leave:BIGINT"
//
// WHY THIS FILE MATTERS
// ---------------------
// Without a shared helper file, different parts of the app start validating
// the same concepts differently.
//
// Example problem:
// - one page accepts "todo:abc"
// - another page rejects it
// - backend rejects both
//
// That causes avoidable inconsistency.
//
// DESIGN GOALS
// ------------
// 1) Keep helpers tiny and predictable
// 2) Match backend validation philosophy where useful
// 3) Stay BIGINT-safe for universal work item keys
// 4) Throw normal JavaScript Error objects with stable `.code` fields
// 5) Never perform any UI behavior here
//
// IMPORTANT
// ---------
// This file is NOT a security boundary.
// Backend validation remains authoritative.
// These helpers only improve:
// - frontend consistency
// - developer ergonomics
// - fail-fast behavior

import axios from "axios";

/* -------------------------------------------------------------------------- */
/* Constants                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Work item key format used across frontend and backend.
 *
 * Supported canonical format:
 *   "<type>:<id>"
 *
 * Examples:
 *   "todo:123"
 *   "leave:9223372036854775807"
 *
 * Validation rules intentionally mirror your backend conventions:
 * - type: letters / digits / "_" / "-"
 * - type length: 1..40
 * - id: digits only
 * - id length: 1..20 (BIGINT-safe as string)
 */
const WORK_ITEM_TYPE_REGEX = /^[a-zA-Z0-9_-]{1,40}$/;
const WORK_ITEM_ID_REGEX = /^[0-9]{1,20}$/;

/* -------------------------------------------------------------------------- */
/* Error helper                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Build a small typed Error object.
 *
 * Why this helps:
 * - callers can inspect err.code instead of parsing message text
 * - still behaves like a normal Error
 *
 * @param {string} code
 * @param {string} message
 * @returns {Error}
 */
function buildTypedError(code, message) {
  const err = new Error(message);
  err.code = code;
  return err;
}

/* -------------------------------------------------------------------------- */
/* Cancellation detection                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Return true when the error represents a NORMAL request cancellation.
 *
 * This is important because request cancellation is expected control flow in your app:
 * - component unmount
 * - route change
 * - request replaced by a newer one
 * - AbortController cleanup
 *
 * Axios v1.x may represent cancellation using:
 * - axios.isCancel(error) === true
 * - error.code === "ERR_CANCELED"
 * - error.name === "CanceledError"
 *
 * We treat all of those as non-errors from a UX perspective.
 *
 * @param {any} error
 * @returns {boolean}
 */
export function isRequestCanceled(error) {
  return (
    axios.isCancel(error) ||
    error?.code === "ERR_CANCELED" ||
    error?.name === "CanceledError"
  );
}



/* -------------------------------------------------------------------------- */
/* API error message translation                                               */
/* -------------------------------------------------------------------------- */

/**
 * Extract a stable backend error code from an Axios error response.
 *
 * Backend controllers in the Leave / HR workflow intentionally return stable
 * technical codes such as "ANNUAL_LIMIT_EXCEEDED" together with an English
 * fallback message.  The frontend must prefer those codes for user-facing UI,
 * because the user's selected language is owned by i18n on the frontend.
 *
 * Keeping this helper in httpUtils avoids repeating subtly different parsing
 * logic in LeaveRequestCreateModal, ApprovalsPage and LeaveWorkItemView.
 *
 * @param {any} error
 * @returns {string}
 */
export function getApiErrorCode(error) {
  const data = error?.response?.data || {};
  const raw = data.code || data.errorCode || error?.code || "";
  return String(raw || "").trim().toUpperCase();
}

/**
 * Build a translated, user-facing API error message.
 *
 * Resolution order:
 * 1) known backend code -> i18n key, for example leaves:errors.ANNUAL_LIMIT_EXCEEDED
 * 2) backend message/error text as a last-resort fallback for unknown codes
 * 3) caller-provided generic fallback
 *
 * Why we still keep the backend message as a fallback:
 * - not every endpoint already returns a fully catalogued code;
 * - during development it is better to show a meaningful server message than a
 *   completely generic error;
 * - known Leave / HR business errors are still translated first, so Polish users
 *   no longer see English backend strings for annual-limit validation.
 *
 * @param {any} error
 * @param {Function} t - i18next translation function from useTranslation()
 * @param {string} fallback
 * @param {{ namespace?: string }} [options]
 * @returns {string}
 */
export function getTranslatedApiErrorMessage(error, t, fallback, options = {}) {
  const namespace = options.namespace || "leaves";
  const data = error?.response?.data || {};
  const code = getApiErrorCode(error);

  if (code && typeof t === "function") {
    const details = data.details || {};
    const ui = details.ui || {};

    const translated = t(`${namespace}:errors.${code}`, {
      ...details,
      ...ui,
      code,
      defaultValue: "",
    });

    if (String(translated || "").trim()) {
      return String(translated).trim();
    }
  }

  const backendMsg = data.message || data.error || error?.message || "";
  return String(backendMsg || fallback || "").trim();
}

/* -------------------------------------------------------------------------- */
/* Basic numeric id validation                                                */
/* -------------------------------------------------------------------------- */

/**
 * Assert that a value is a valid positive integer id.
 *
 * Intended for classic INT-based identifiers such as:
 * - todo ids
 * - user ids
 * - small table row ids
 *
 * IMPORTANT:
 * - This helper is NOT appropriate for BIGINT ids that must stay strings.
 * - For BIGINT-safe work item ids, use parseWorkItemKey() instead.
 *
 * @param {any} id
 * @param {string} [label="id"]
 * @returns {number} normalized positive integer
 * @throws {Error} with code="INVALID_ID"
 */
export function assertValidId(id, label = "id") {
  const n = Number(id);

  if (!Number.isFinite(n) || !Number.isInteger(n) || n <= 0) {
    throw buildTypedError("INVALID_ID", `Invalid ${label}: ${id}`);
  }

  return n;
}

/* -------------------------------------------------------------------------- */
/* Work item key validation / parsing                                         */
/* -------------------------------------------------------------------------- */

/**
 * Parse and normalize a universal work item key.
 *
 * Accepted input examples:
 *   "todo:123"
 *   "TODO:123"
 *   " leave:9223372036854775807 "
 *
 * Returned output:
 *   {
 *     key: "todo:123",
 *     type: "todo",
 *     id: "123"
 *   }
 *
 * IMPORTANT:
 * - id is always returned as STRING
 * - this is deliberate to remain BIGINT-safe
 *
 * @param {any} workItemKey
 * @returns {{ key: string, type: string, id: string }}
 * @throws {Error} with code="INVALID_WORK_ITEM_KEY"
 */
export function parseWorkItemKey(workItemKey) {
  const raw = String(workItemKey || "").trim();

  if (!raw) {
    throw buildTypedError("INVALID_WORK_ITEM_KEY", "Invalid workItemKey: value is empty.");
  }

  if (raw.length > 128) {
    throw buildTypedError("INVALID_WORK_ITEM_KEY", "Invalid workItemKey: value is too long.");
  }

  const parts = raw.split(":");
  if (parts.length !== 2) {
    throw buildTypedError(
      "INVALID_WORK_ITEM_KEY",
      "Invalid workItemKey format. Expected 'type:id'."
    );
  }

  const typeRaw = String(parts[0] || "").trim();
  const idRaw = String(parts[1] || "").trim();

  if (!WORK_ITEM_TYPE_REGEX.test(typeRaw)) {
    throw buildTypedError(
      "INVALID_WORK_ITEM_KEY",
      "Invalid workItemKey type. Allowed characters: letters, digits, '_' and '-'."
    );
  }

  if (!WORK_ITEM_ID_REGEX.test(idRaw)) {
    throw buildTypedError(
      "INVALID_WORK_ITEM_KEY",
      "Invalid workItemKey id. Expected a digits-only string (1..20 digits)."
    );
  }

  const type = typeRaw.toLowerCase();
  const id = idRaw;

  return {
    key: `${type}:${id}`,
    type,
    id,
  };
}

/**
 * Assert that a universal work item key is valid and return the normalized key.
 *
 * This is the convenience helper most service files should use when they only need
 * the final normalized key string.
 *
 * Example:
 *   const key = assertWorkItemKey("TODO:123");
 *   // => "todo:123"
 *
 * @param {any} workItemKey
 * @returns {string}
 * @throws {Error} with code="INVALID_WORK_ITEM_KEY"
 */
export function assertWorkItemKey(workItemKey) {
  return parseWorkItemKey(workItemKey).key;
}

/**
 * Return true if the given value is a syntactically valid work item key.
 *
 * Useful when:
 * - you want a safe boolean check
 * - you do NOT want to throw
 *
 * @param {any} workItemKey
 * @returns {boolean}
 */
export function isValidWorkItemKey(workItemKey) {
  try {
    parseWorkItemKey(workItemKey);
    return true;
  } catch {
    return false;
  }
}

/* -------------------------------------------------------------------------- */
/* Type-specific helpers                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Parse a ToDo work item key and return a classic numeric todo id.
 *
 * Example:
 *   parseTodoKey("todo:123") => 123
 *
 * @param {any} workItemKey
 * @returns {number}
 * @throws {Error}
 */
export function parseTodoKey(workItemKey) {
  const parsed = parseWorkItemKey(workItemKey);

  if (parsed.type !== "todo") {
    throw buildTypedError(
      "INVALID_WORK_ITEM_TYPE",
      `Expected a todo work item key, received '${parsed.type}:${parsed.id}'.`
    );
  }

  return assertValidId(parsed.id, "todo id");
}

/**
 * Parse a Leave work item key and return a BIGINT-safe string id.
 *
 * Example:
 *   parseLeaveKey("leave:9223372036854775807") => "9223372036854775807"
 *
 * IMPORTANT:
 * - returned as string by design
 * - do NOT convert to Number
 *
 * @param {any} workItemKey
 * @returns {string}
 * @throws {Error}
 */
export function parseLeaveKey(workItemKey) {
  const parsed = parseWorkItemKey(workItemKey);

  if (parsed.type !== "leave") {
    throw buildTypedError(
      "INVALID_WORK_ITEM_TYPE",
      `Expected a leave work item key, received '${parsed.type}:${parsed.id}'.`
    );
  }

  return parsed.id;
}

